/**
 * SADARayon — Modale d'audit Mediciel pour un inventaire.
 *
 * Compare le stock_theorique de chaque ligne (snapshot figé au démarrage OU
 * lors du dernier resnapshot) avec le stock Mediciel actuel.
 *
 * Permet de :
 *   - voir les écarts entre snapshot et catalogue Mediciel
 *   - resnapshoter l'inventaire pour réaligner sur Mediciel
 *   - filtrer pour n'afficher que les lignes en drift
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2, FileQuestion } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const InventoryStockAuditDialog = ({ open, inventoryId, zoneName, onClose, onRefreshed }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('drift'); // 'all' | 'drift' | 'missing'

  const reload = useCallback(async () => {
    if (!inventoryId) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/inventory/inventories/${inventoryId}/stock-audit`,
        { headers: auth() },
      );
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement audit');
    } finally {
      setLoading(false);
    }
  }, [inventoryId]);

  useEffect(() => { if (open) reload(); }, [open, reload]);

  const onResnapshot = async () => {
    if (!window.confirm(
      'Resnapshoter le stock théorique va remplacer toutes les valeurs actuelles\n'
      + 'par celles de Mediciel à cet instant.\n\n'
      + 'Si des saisies OP1 existent déjà, leur écart sera recalculé.\n\nContinuer ?',
    )) return;
    setRefreshing(true);
    try {
      const res = await axios.post(
        `${API}/inventory/inventories/${inventoryId}/refresh-snapshot`,
        {},
        { headers: auth() },
      );
      toast.success(
        `${res.data?.lines_changed || 0} ligne(s) mises à jour sur ${res.data?.lines_touched || 0} (snapshot du ${fmtDateTime(res.data?.refreshed_at)})`,
        { duration: 7000 },
      );
      await reload();
      onRefreshed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur resnapshot');
    } finally {
      setRefreshing(false);
    }
  };

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (filter === 'drift') return data.rows.filter((r) => r.in_catalog && r.diff !== 0);
    if (filter === 'missing') return data.rows.filter((r) => !r.in_catalog);
    return data.rows;
  }, [data, filter]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="stock-audit-dialog">
        <DialogHeader>
          <DialogTitle>Audit Mediciel — {zoneName || 'Inventaire'}</DialogTitle>
        </DialogHeader>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
              <div className="text-zinc-500">Snapshot démarré</div>
              <div className="font-medium text-zinc-900">{fmtDateTime(data.inventory_started_at)}</div>
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
              <div className="text-zinc-500">Dernière actualisation snapshot</div>
              <div className="font-medium text-zinc-900">{fmtDateTime(data.last_stock_refresh_at)}</div>
            </div>
            <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2">
              <div className="text-zinc-500">Dernière sync Mediciel</div>
              <div className="font-medium text-zinc-900">{fmtDateTime(data.mediciel_last_sync_at)}</div>
            </div>
            <div className={`rounded-md border p-2 ${data.lines_with_drift ? 'border-amber-300 bg-amber-50' : 'border-emerald-300 bg-emerald-50'}`}>
              <div className={data.lines_with_drift ? 'text-amber-700' : 'text-emerald-700'}>Écart snapshot ↔ Mediciel</div>
              <div className={`font-mono font-bold ${data.lines_with_drift ? 'text-amber-900' : 'text-emerald-900'}`}>
                {data.lines_with_drift} ligne(s) sur {data.lines_total}
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 mt-2">
          {[
            { id: 'drift', label: 'Lignes en écart', icon: AlertTriangle, color: 'amber' },
            { id: 'missing', label: 'Hors catalogue', icon: FileQuestion, color: 'rose' },
            { id: 'all', label: 'Toutes', icon: CheckCircle2, color: 'zinc' },
          ].map((f) => {
            const Icon = f.icon;
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border transition ${
                  active
                    ? `bg-${f.color}-600 text-white border-${f.color}-600`
                    : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'
                }`}
                data-testid={`audit-filter-${f.id}`}
              >
                <Icon className="w-3.5 h-3.5" /> {f.label}
              </button>
            );
          })}
          <div className="flex-1" />
          <Button
            size="sm" variant="outline"
            onClick={reload}
            disabled={loading}
            data-testid="audit-reload"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        <div className="overflow-auto flex-1 rounded-md border border-zinc-200 mt-2">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
              <tr className="text-left text-zinc-600">
                <th className="px-2 py-1.5 font-medium">Produit</th>
                <th className="px-2 py-1.5 font-medium">Code EAN / ID</th>
                <th className="px-2 py-1.5 font-medium text-right">Snapshot</th>
                <th className="px-2 py-1.5 font-medium text-right">Mediciel actuel</th>
                <th className="px-2 py-1.5 font-medium text-right">Diff</th>
                <th className="px-2 py-1.5 font-medium text-right">Comptage</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.line_id} className={`border-b border-zinc-100 ${r.diff && r.diff !== 0 ? 'bg-amber-50/50' : ''} ${!r.in_catalog ? 'bg-rose-50/40' : ''}`}>
                  <td className="px-2 py-1.5">
                    <div className="font-medium text-zinc-900 truncate max-w-xs">{r.product_name}</div>
                    {r.code_1 && <div className="text-[10px] text-zinc-500">Code Mediciel : {r.code_1}</div>}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-zinc-600 text-[11px]">
                    <div>{r.code_ean || '—'}</div>
                    {r.mediciel_id && <div className="text-zinc-400">id: {String(r.mediciel_id).slice(0, 16)}</div>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">{r.stock_theorique_snapshot}</td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {r.in_catalog ? r.stock_mediciel_current : <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px]">absent</Badge>}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums">
                    {r.diff === null ? '—' : (
                      r.diff === 0
                        ? <span className="text-emerald-600">0</span>
                        : <span className={r.diff > 0 ? 'text-blue-700 font-bold' : 'text-amber-700 font-bold'}>{r.diff > 0 ? '+' : ''}{r.diff}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono tabular-nums text-zinc-700">
                    {r.stock_physique == null ? '—' : r.stock_physique}
                  </td>
                </tr>
              ))}
              {!filteredRows.length && !loading && (
                <tr><td colSpan={6} className="px-2 py-6 text-center text-zinc-500">Aucune ligne pour ce filtre.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <DialogFooter className="flex items-center justify-between gap-2 mt-2">
          <p className="text-xs text-zinc-500">
            Diff &gt; 0 = stock Mediciel a augmenté · Diff &lt; 0 = stock Mediciel a baissé depuis le snapshot.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} disabled={refreshing}>Fermer</Button>
            <Button
              onClick={onResnapshot}
              disabled={refreshing || loading}
              className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
              data-testid="audit-resnapshot"
            >
              {refreshing
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <RefreshCw className="w-4 h-4 mr-2" />}
              Resnapshoter depuis Mediciel
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InventoryStockAuditDialog;
